import { useCallback, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { LogIn, Plus } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { USER_ROLES } from '@/lib/constants/roles';
import { usePermission } from '@/lib/permissions/use-permission';
import { usePermissionStore } from '@/lib/permissions/store';
import { formatDate, formatDateTime } from '@/lib/utils/date';
import { useSwitchUser, useUsers } from '../api';
import type { UserListFilterBy, UserRow } from '../types';
import { BadgeList } from '../components/BadgeList';
import { UserRowActions } from '../components/UserRowActions';
import { UserStatusBadge } from '../components/UserStatusBadge';

/**
 * Radix Select reserves the empty string as "no value". Use a sentinel
 * `'none'` in the local state and translate to the empty-string filterBy
 * the API expects only when calling the API.
 */
type FilterChoice = UserListFilterBy | 'none';

const FILTER_OPTIONS: Array<{ value: FilterChoice; label: string; numeric?: boolean }> = [
  { value: 'none', label: 'No filter' },
  { value: 'user_id', label: 'User ID', numeric: true },
  { value: 'username', label: 'Username' },
  { value: 'parent', label: 'Parent ID', numeric: true },
  { value: 'parent_user', label: 'Parent username' },
];

export function UserListPage() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());
  const switchTo = useAuthStore((s) => s.switchTo);
  const canCreateUser = usePermission('Manage Users.Create User', BASE_PERMISSION.ADD);

  // TTS Balance / TTS Plan columns follow the "TTS Plans" ACL:
  //  - superadmin: always; sub-superadmin: their own ACL
  //  - reseller: the ACL of the sub-superadmin above them (null = no restriction)
  const canViewTtsPlans = usePermission('TTS Plans', BASE_PERMISSION.VIEW);
  const inheritedPerms = usePermissionStore((s) => s.inheritedPerms);
  const showTtsColumns =
    role === USER_ROLES.ADMIN
      ? !inheritedPerms || Boolean(inheritedPerms['TTS Plans']?.view_permission)
      : canViewTtsPlans;

  const [filterChoice, setFilterChoice] = useState<FilterChoice>('none');
  const [filterDraft, setFilterDraft] = useState('');
  const [filterData, setFilterData] = useState('');

  const filterBy: UserListFilterBy = filterChoice === 'none' ? '' : filterChoice;
  const filterArgs = filterBy && filterData ? { filterBy, filterData } : undefined;
  const usersQuery = useUsers(filterArgs ? { filter: filterArgs } : {});
  const switchMutation = useSwitchUser();

  const users = usersQuery.data?.users ?? [];

  const handleSwitch = useCallback(
    (row: UserRow) => {
      switchMutation.mutate(row.userId, {
                onSuccess: (res) => {
           if (!res.token) {
                       toast.error((res as { message?: string }).message || 'Cannot switch — user account may be expired or deleted');
            return;
          }
          const incomingRole = Array.isArray(res.role) ? res.role[0] : res.role;
          if (!incomingRole) {
            toast.error('Switch response missing role');
            return;
          }
          switchTo({
            user: { userId: String(res.userid), role: incomingRole },
            token: res.token,
          });
          toast.success(`Switched to ${row.username}`);
          const isSuser = incomingRole === USER_ROLES.SUB_USER;
          navigate(isSuser ? '/sub-user-report' : '/', { replace: true });
        },
        onError: () => toast.error('Cannot switch — user account may be expired or deleted'),
      });
    },
    [navigate, switchMutation, switchTo],
  );

  /**
   * Column order requested:
   *   ID → Username → Type → Company → Status → Location → Group → Module
   *   → (the rest, scrollable) → Switch User (pinned right) → Actions (pinned right)
   */
  const columns = useMemo<ColumnDef<UserRow>[]>(
    () => [
      {
        accessorKey: 'userId',
        header: 'ID',
        size: 70,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.userId}</span>
        ),
      },
      {
        accessorKey: 'username',
        header: 'Username',
        cell: ({ row }) => (
          <Link
            to={`/update-user/${row.original.userId}`}
            className="font-medium text-foreground hover:underline"
          >
            {row.original.username}
          </Link>
        ),
      },
      {
        accessorKey: 'userType',
        header: 'Type',
        size: 100,
        cell: ({ row }) => <Badge variant="outline">{row.original.userType}</Badge>,
      },
      {
        accessorKey: 'company',
        header: 'Company',
      },
      {
        id: 'status',
        header: 'Status',
        size: 110,
        cell: ({ row }) => <UserStatusBadge user={row.original} />,
      },
      {
        id: 'locations',
        header: 'Location',
        enableSorting: false,
        cell: ({ row }) => (
          <BadgeList
            items={extractNames(
              row.original.locations ?? row.original.location,
              row.original.userLocations,
              'locationName',
            )}
          />
        ),
      },
      {
        id: 'groups',
        header: 'Group',
        enableSorting: false,
        cell: ({ row }) => (
          <BadgeList
            items={extractNames(
              row.original.groups ?? row.original.group,
              row.original.userGroups,
              'groupName',
            )}
          />
        ),
      },
      {
        id: 'modules',
        header: 'Module',
        enableSorting: false,
        cell: ({ row }) => (
          <BadgeList
            items={extractNames(
              row.original.modules,
              row.original.userModules,
              'moduleName',
            )}
          />
        ),
      },
      {
        accessorKey: 'credits',
        header: 'Voice Balance',
        size: 120,
        cell: ({ row }) => (
          <span className="tabular-nums">
            ₹{Number(row.original.credits ?? 0).toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: 'planName',
        header: 'Voice Plan',
        cell: ({ row }) => row.original.planName ?? '—',
      },
      ...(showTtsColumns ? ([
      {
        accessorKey: 'ttsCredits',
        header: 'TTS Balance',
        size: 110,
        cell: ({ row }) => (
          <span className="tabular-nums">
            ₹{Number(row.original.ttsCredits ?? 0).toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: 'ttsPlanName',
        header: 'TTS Plan',
        cell: ({ row }) => row.original.ttsPlanName ?? '—',
      },
      ] as ColumnDef<UserRow>[]) : []),
      {
        accessorKey: 'accountType',
        header: 'Account',
        size: 130,
        cell: ({ row }) => (
          <Badge variant={row.original.accountType === 1 ? 'default' : row.original.accountType === 3 ? 'outline' : 'secondary'}>
              {row.original.accountType === 1 ? 'Transactional' : row.original.accountType === 3 ? 'Both' : 'Promotional'}
          </Badge>
        ),
      },
      {
        accessorKey: 'parentUser',
        header: 'Parent',
        cell: ({ row }) => row.original.parentUser ?? '—',
      },
      {
        accessorKey: 'expiryDate',
        header: 'Expiry',
        size: 130,
        cell: ({ row }) => {
          const v = row.original.expiryDate;
          if (!v) return '—';
          const date = new Date(v.replace(' ', 'T'));
          return Number.isFinite(date.getTime()) ? formatDate(date) : v;
        },
      },
      {
        accessorKey: 'reqDate',
        header: 'Created',
        size: 170,
        cell: ({ row }) => formatDateTime(row.original.reqDate),
      },
      {
        id: 'switchUser',
        header: 'Switch',
        size: 76,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) =>
          role !== USER_ROLES.USER ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              title={`Switch to ${row.original.username}`}
              onClick={() => handleSwitch(row.original)}
              disabled={switchMutation.isPending}
            >
              <LogIn className="h-4 w-4" />
            </Button>
          ) : null,
      },
      {
        id: 'actions',
        header: 'Settings',
        size: 90,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <UserRowActions
            user={row.original}
            editPathPrefix="/update-user"
            hideSwitchUser={role === USER_ROLES.USER}
          />
        ),
      },
    ],
    [role, switchMutation.isPending, handleSwitch, showTtsColumns],
  );

  const applyFilter = () => {
    setFilterData(filterDraft);
  };

  const clearFilter = () => {
    setFilterChoice('none');
    setFilterData('');
    setFilterDraft('');
  };

  const numericFilter = FILTER_OPTIONS.find((o) => o.value === filterChoice)?.numeric ?? false;
  const canView = usePermission('Manage Users.Directory', BASE_PERMISSION.VIEW);

  if (!canView) return <Navigate to="/" replace />;

  return (
    <FillPage>
      <PageHeader
        title="Directory"
        description={
          isSubOrSuper
            ? 'All managed users across the platform.'
            : 'Users under your account.'
        }
        actions={
          canCreateUser ? (
            <Button asChild>
              <Link to="/create-user">
                <Plus className="h-4 w-4" />
                Add user
              </Link>
            </Button>
          ) : undefined
        }
      />

      <DataTable<UserRow, unknown>
        // v6 — picks up the FillPage flex layout where the table fills
        // remaining vertical space and the header pins inside it.
        tableId="users:main:v6"
        columns={columns}
        data={users}
        isLoading={usersQuery.isLoading}
        searchPlaceholder="Search users…"
        emptyTitle="No users"
        emptyDescription={
          filterArgs
            ? 'No users match the active filter.'
            : 'No users exist under your account yet.'
        }
        getRowId={(row) => String(row.userId)}
        exportFilename="users.csv"
        initialColumnPinning={{ right: ['switchUser', 'actions'] }}
        pageSize={10}
        beforeTable={
          isSubOrSuper && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border bg-card px-3 py-2">
              <span className="text-xs font-medium text-muted-foreground">Filter:</span>
              <Select
                value={filterChoice}
                onValueChange={(v) => {
                  setFilterChoice(v as FilterChoice);
                  setFilterDraft('');
                  setFilterData('');
                }}
              >
                <SelectTrigger className="h-8 w-44">
                  <SelectValue placeholder="No filter" />
                </SelectTrigger>
                <SelectContent>
                  {FILTER_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filterChoice !== 'none' && (
                <>
                  <Input
                    value={filterDraft}
                    inputMode={numericFilter ? 'numeric' : 'text'}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFilterDraft(numericFilter ? v.replace(/[^\d]/g, '') : v);
                    }}
                    placeholder={numericFilter ? 'Enter ID' : 'Enter value'}
                    className="h-8 w-48"
                  />
                  <Button size="sm" onClick={applyFilter} disabled={!filterDraft}>
                    Apply
                  </Button>
                  <Button variant="ghost" size="sm" onClick={clearFilter}>
                    Clear
                  </Button>
                </>
              )}
            </div>
          )
        }
      />
    </FillPage>
  );
}

/**
 * The user-list endpoints return joined relations in TWO shapes:
 *   - Object arrays (`groups`, `locations`, `modules`) from per-user
 *     detail endpoints like /api/obd/user/profile/:id.
 *   - CSV strings (`userGroups`, `userLocations`, `userModules`) from
 *     the listing endpoints (/api/obd/user/:id and /filter/user/list),
 *     produced server-side by GROUP_CONCAT. We accept either shape and
 *     normalize to a flat string[] for the BadgeList.
 */
function extractNames<TKey extends string>(
  objectList: Array<Record<TKey, string>> | undefined,
  csv: string | undefined,
  nameKey: TKey,
): string[] {
  if (objectList && objectList.length > 0) {
    return objectList.map((o) => o[nameKey]).filter(Boolean);
  }
  if (typeof csv === 'string' && csv.trim().length > 0) {
    return csv
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}
